import { useCallback, useEffect, useState } from "react";
import { getRecentMatchesForPlayer } from "../../../services/matchHistoryService";

const HISTORY_CACHE_TTL_MS = 45_000;

/** @type {{ key: string|null, at: number, items: object[] }} */
let historyCache = { key: null, at: 0, items: [] };

function cacheKey(playerId, limit, sportId) {
  return `${playerId}:${limit}:${sportId || "all"}`;
}

function readHistoryCache(playerId, limit, sportId) {
  const key = cacheKey(playerId, limit, sportId);
  if (historyCache.key !== key) return null;
  if (Date.now() - historyCache.at > HISTORY_CACHE_TTL_MS) return null;
  return historyCache.items;
}

function writeHistoryCache(playerId, limit, sportId, items) {
  historyCache = {
    key: cacheKey(playerId, limit, sportId),
    at: Date.now(),
    items: Array.isArray(items) ? items : [],
  };
}

/**
 * @param {string | null | undefined} playerId
 * @param {number} [limit]
 */
export function usePlayerMatchHistory(playerId, limit = 5, sportId = null) {
  const cached = playerId ? readHistoryCache(playerId, limit, sportId) : null;
  const [items, setItems] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => Boolean(playerId) && !cached);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    if (playerId) {
      historyCache = { key: null, at: 0, items: [] };
    }
    setTick((n) => n + 1);
  }, [playerId]);

  useEffect(() => {
    if (!playerId) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const stale = readHistoryCache(playerId, limit, sportId);
    if (stale) {
      setItems(stale);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    getRecentMatchesForPlayer(playerId, limit, sportId || null)
      .then((rows) => {
        if (!active) return;
        writeHistoryCache(playerId, limit, sportId, rows);
        setItems(rows);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [playerId, limit, sportId, tick]);

  return { items, loading, error, reload };
}
